/**
 * The widget's own copy, in the language of the page it is embedded in.
 *
 * Everything here is a DEFAULT. A client who sets `greeting` or
 * `defaultSuggestions` in their snippet (or, since 1.6.0, in the console —
 * see config.ts) still wins — this is what the widget says when it has not
 * been told, and it used to say it in English on every site in the world.
 *
 * That was not only untidy, it broke retrieval. The default chips read "What
 * services do you offer?" on Ecobel's Italian site, and the assistant searches
 * its corpus by embedding the question: measured against those 60 live Italian
 * pages, the English question admitted ZERO of them and the Italian one
 * admitted two. Clicking the widget's own suggestion was a guaranteed miss, and
 * the visitor was told the business had no information at all.
 *
 * Languages are the five the company can actually review. Anything else falls
 * back to English rather than to a machine translation nobody has read — and
 * the assistant still ANSWERS in the visitor's language, because that is the
 * backend's job (common/languages.py), not this table's.
 *
 * Templates: a few strings carry a `{name}` placeholder — fill them with
 * `fill()` rather than concatenating, so word order stays the translator's.
 */

export interface WidgetStrings {
  subtitle: string;
  greeting: string;
  suggestions: string[];
  errorMessage: string;
  originBlockedMessage: string;
  /**
   * Canned backend replies, keyed by the `code` the API sends beside them.
   * The server's `reply` text is English; a visitor reading Italian gets
   * these instead (shared-chatbot review S10).
   */
  noContent: string;
  overCap: string;
  unavailable: string;
  refused: string;
  privacyNotice: string;
  privacyLinkLabel: string;
  typeAMessage: string;
  sendMessage: string;
  openChat: string;
  closeChat: string;
  chatWithUs: string;
  /**
   * The visible pill beside the closed launcher on a desktop viewport — the
   * invitation, in the visitor's words: they talk to the website, and it
   * answers. `launcherLabel` on the snippet overrides it; `false` hides it.
   */
  talkToSite: string;
  /** `{name}` = business name. Accessible name of the dialog. */
  chatDialogLabel: string;
  /** `{name}` = business name. Label above a human operator's reply. */
  teamLabel: string;
  typing: string;
  tryAgain: string;
  newChat: string;
  confirmNewChat: string;
  tapAgainToClear: string;
  tapAgainToClearShort: string;
  expandChat: string;
  collapseChat: string;
  dismissSuggestions: string;
  /** Lead capture (POST /chat/lead). */
  leaveDetails: string;
  leadTitle: string;
  leadSubmit: string;
  leadSending: string;
  leadCancel: string;
  /** `{name}` = the name the visitor typed. */
  leadThanks: string;
  leadNameRequired: string;
  leadEmailInvalid: string;
  leadTooLong: string;
  leadError: string;
  leadCap: string;
  /** The form a business sets (ADR-114 Am.2). A required phone says `bookingPhoneRequired`. */
  leadMessageLabel: string;
  leadMessageRequired: string;
  leadFieldRequired: string;
  /** The empty first choice of a dropdown question. */
  leadChoose: string;
  /**
   * The ONE details form's fields (DetailsForm.tsx, ADR-110 Am.6), the same
   * whether the visitor leaves their details or books. A required field is
   * its label and a visual `*`; an optional one says so in words with
   * `detailsOptional`, where `{label}` is the field's label — for a question
   * the business added, its own words as typed.
   */
  detailsName: string;
  detailsEmail: string;
  detailsPhone: string;
  detailsOptional: string;
  /**
   * Booking in the conversation (ADR-110). The details form's Cancel,
   * Sending… and validation copy is the lead's (leadCancel, leadSending,
   * leadNameRequired, leadEmailInvalid, leadTooLong).
   */
  bookingDetailsTitle: string;
  bookingContinue: string;
  bookingPhoneRequired: string;
  /**
   * `{name}` = the name the visitor typed. Their bubble, and the message sent,
   * after the details form — it must never carry the email.
   */
  bookingUnderName: string;
  /** The same line when the form asked for no name. */
  bookingDetailsSent: string;
  /** The confirm buttons. What is SENT is always `yes` / `no`, in English. */
  bookingConfirmYes: string;
  bookingConfirmNo: string;
  /** The title of the card a finished booking shows. */
  bookingBooked: string;
  /** `{id}` = the booking reference. */
  bookingReference: string;
  /**
   * Feedback about the assistant (feedback.ts), filed with the Ema Answers
   * team. The form reuses the lead form's Cancel, Sending…, invalid-email and
   * too-long copy. `feedbackIntro` is the first thing the form says and must
   * keep naming WHO reads it: it is the visitor's notice that this does not
   * go to the business whose site they are on.
   */
  sendFeedback: string;
  backToChat: string;
  feedbackSubtitle: string;
  feedbackIntro: string;
  feedbackTypeLabel: string;
  feedbackTypeGeneral: string;
  feedbackTypeFeature: string;
  feedbackTypeBug: string;
  feedbackTypeLove: string;
  feedbackMessage: string;
  feedbackPlaceholder: string;
  feedbackEmail: string;
  feedbackSubmit: string;
  feedbackMessageRequired: string;
  feedbackThanks: string;
  feedbackError: string;
  /**
   * The human check (ADR-123). `humanCheckTitle` names the frame for assistive
   * tech while Cloudflare's challenge is on screen; `humanCheckFailed` is the
   * one sentence for every way a check can fail — the reason goes to the
   * console, where whoever runs the site can act on it.
   */
  humanCheckTitle: string;
  humanCheckFailed: string;
}

const en: WidgetStrings = {
  subtitle: 'AI Assistant',
  greeting: 'Hi! How can I help you today?',
  suggestions: ['What services do you offer?', 'Show me pricing', 'Tell me more'],
  errorMessage: "Sorry, I'm having trouble connecting. Please try again.",
  originBlockedMessage: "This assistant isn't available on this site.",
  noContent: "I don't have any information to answer from just yet. Please check back soon.",
  overCap: 'This assistant has reached its usage limit for now. Please try again later.',
  unavailable: "Sorry, the assistant isn't available right now.",
  refused: "Sorry, I can't help with that request.",
  privacyNotice: 'Messages are stored to provide this service.',
  privacyLinkLabel: 'Privacy Policy',
  typeAMessage: 'Type a message...',
  sendMessage: 'Send message',
  openChat: 'Open chat',
  closeChat: 'Close chat',
  chatWithUs: 'Chat with us',
  talkToSite: 'Talk to our website',
  chatDialogLabel: '{name} chat',
  teamLabel: '{name} team',
  typing: 'The assistant is typing',
  tryAgain: 'Try again',
  newChat: 'New chat',
  confirmNewChat: 'Confirm new chat — this clears the conversation',
  tapAgainToClear: 'Tap again to clear this conversation',
  tapAgainToClearShort: 'Tap again to clear',
  expandChat: 'Expand chat',
  collapseChat: 'Collapse chat',
  dismissSuggestions: 'Dismiss suggestions',
  leaveDetails: 'Leave your details',
  leadTitle: "Leave your details and we'll get back to you.",
  leadSubmit: 'Send',
  leadSending: 'Sending…',
  leadCancel: 'Cancel',
  leadThanks: "Thanks {name}! We've received your details and will be in touch soon.",
  leadNameRequired: 'Please enter your name.',
  leadEmailInvalid: 'Please enter a valid email address.',
  leadTooLong: "That's a bit long — please shorten it.",
  leadError: "Sorry, we couldn't send your details. Please try again.",
  leadCap: "We're receiving a lot of enquiries right now. Please try again later.",
  leadMessageLabel: 'Message',
  leadMessageRequired: 'Please enter a message.',
  detailsOptional: '{label} (optional)',
  leadFieldRequired: 'Please fill in “{label}”.',
  leadChoose: 'Choose…',
  bookingDetailsTitle: 'Your details for the booking',
  detailsName: 'Name',
  detailsEmail: 'Email',
  detailsPhone: 'Phone',
  bookingContinue: 'Continue',
  bookingPhoneRequired: 'Please enter your phone number.',
  bookingUnderName: 'Booking under {name}',
  bookingDetailsSent: 'Here are my details',
  bookingConfirmYes: 'Yes, book it',
  bookingConfirmNo: 'No',
  bookingBooked: 'Booked',
  bookingReference: 'Reference {id}',
  sendFeedback: 'Send feedback',
  backToChat: 'Back to chat',
  feedbackSubtitle: 'Feedback about this assistant',
  feedbackIntro: 'Your feedback goes to Ema Answers, the team that makes this assistant.',
  feedbackTypeLabel: 'What is it about?',
  feedbackTypeGeneral: 'General',
  feedbackTypeFeature: 'Suggestion',
  feedbackTypeBug: 'Problem',
  feedbackTypeLove: 'Praise',
  feedbackMessage: 'Your feedback',
  feedbackPlaceholder: "What worked, what didn't, what would help?",
  feedbackEmail: 'Email (optional, if you would like a reply)',
  feedbackSubmit: 'Send feedback',
  feedbackMessageRequired: 'Please write your feedback first.',
  feedbackThanks: 'Thank you — your feedback has been sent.',
  feedbackError: "Sorry, we couldn't send your feedback. Please try again.",
  humanCheckTitle: 'Security check',
  humanCheckFailed: "Sorry, we couldn't confirm you're a person. Please try again.",
};

const it: WidgetStrings = {
  subtitle: 'Assistente AI',
  greeting: 'Ciao! Come posso aiutarti oggi?',
  suggestions: ['Quali servizi offrite?', 'Mostrami i prezzi', 'Dimmi di più'],
  errorMessage: 'Scusa, ho problemi di connessione. Riprova.',
  originBlockedMessage: 'Questo assistente non è disponibile su questo sito.',
  noContent: 'Non ho ancora informazioni per rispondere. Riprova tra poco.',
  overCap: 'Questo assistente ha raggiunto il limite di utilizzo per ora. Riprova più tardi.',
  unavailable: "Scusa, l'assistente non è disponibile in questo momento.",
  refused: 'Mi dispiace, non posso aiutarti con questa richiesta.',
  privacyNotice: 'I messaggi vengono conservati per fornire questo servizio.',
  privacyLinkLabel: 'Informativa sulla privacy',
  typeAMessage: 'Scrivi un messaggio...',
  sendMessage: 'Invia il messaggio',
  openChat: 'Apri la chat',
  closeChat: 'Chiudi la chat',
  chatWithUs: 'Scrivici',
  talkToSite: 'Parla con il nostro sito',
  chatDialogLabel: 'Chat di {name}',
  teamLabel: 'Team {name}',
  typing: "L'assistente sta scrivendo",
  tryAgain: 'Riprova',
  newChat: 'Nuova chat',
  confirmNewChat: 'Conferma la nuova chat — la conversazione verrà cancellata',
  tapAgainToClear: 'Tocca di nuovo per cancellare questa conversazione',
  tapAgainToClearShort: 'Tocca di nuovo per cancellare',
  expandChat: 'Espandi la chat',
  collapseChat: 'Riduci la chat',
  dismissSuggestions: 'Nascondi i suggerimenti',
  leaveDetails: 'Lascia i tuoi dati',
  leadTitle: 'Lascia i tuoi dati e ti ricontatteremo.',
  leadSubmit: 'Invia',
  leadSending: 'Invio in corso…',
  leadCancel: 'Annulla',
  leadThanks: 'Grazie {name}! Abbiamo ricevuto i tuoi dati e ti ricontatteremo presto.',
  leadNameRequired: 'Inserisci il tuo nome.',
  leadEmailInvalid: 'Inserisci un indirizzo email valido.',
  leadTooLong: 'Il testo è troppo lungo: accorcialo, per favore.',
  leadError: 'Scusa, non siamo riusciti a inviare i tuoi dati. Riprova.',
  leadCap: 'Stiamo ricevendo molte richieste in questo momento. Riprova più tardi.',
  leadMessageLabel: 'Messaggio',
  leadMessageRequired: 'Scrivi un messaggio.',
  detailsOptional: '{label} (facoltativo)',
  leadFieldRequired: 'Compila il campo “{label}”.',
  leadChoose: 'Scegli…',
  bookingDetailsTitle: 'I tuoi dati per la prenotazione',
  detailsName: 'Nome',
  detailsEmail: 'Email',
  detailsPhone: 'Telefono',
  bookingContinue: 'Continua',
  bookingPhoneRequired: 'Inserisci il tuo numero di telefono.',
  bookingUnderName: 'Prenotazione a nome di {name}',
  bookingDetailsSent: 'Ecco i miei dati',
  bookingConfirmYes: 'Sì, prenota',
  bookingConfirmNo: 'No',
  bookingBooked: 'Prenotazione confermata',
  bookingReference: 'Riferimento {id}',
  sendFeedback: 'Invia un feedback',
  backToChat: 'Torna alla chat',
  feedbackSubtitle: 'Feedback su questo assistente',
  feedbackIntro: 'Il tuo feedback arriva a Ema Answers, il team che realizza questo assistente.',
  feedbackTypeLabel: 'Di cosa si tratta?',
  feedbackTypeGeneral: 'Generale',
  feedbackTypeFeature: 'Suggerimento',
  feedbackTypeBug: 'Problema',
  feedbackTypeLove: 'Complimento',
  feedbackMessage: 'Il tuo feedback',
  feedbackPlaceholder: 'Cosa ha funzionato, cosa no, cosa ti sarebbe utile?',
  feedbackEmail: 'Email (facoltativa, se vuoi una risposta)',
  feedbackSubmit: 'Invia il feedback',
  feedbackMessageRequired: 'Scrivi prima il tuo feedback.',
  feedbackThanks: 'Grazie, il tuo feedback è stato inviato.',
  feedbackError: 'Scusa, non siamo riusciti a inviare il tuo feedback. Riprova.',
  humanCheckTitle: 'Controllo di sicurezza',
  humanCheckFailed: 'Scusa, non siamo riusciti a verificare che tu sia una persona. Riprova.',
};

const fr: WidgetStrings = {
  subtitle: 'Assistant IA',
  greeting: 'Bonjour ! Comment puis-je vous aider ?',
  suggestions: ['Quels services proposez-vous ?', 'Voir les tarifs', 'En savoir plus'],
  errorMessage: "Désolé, je n'arrive pas à me connecter. Veuillez réessayer.",
  originBlockedMessage: "Cet assistant n'est pas disponible sur ce site.",
  noContent: "Je n'ai pas encore d'informations pour répondre. Revenez bientôt.",
  overCap: "Cet assistant a atteint sa limite d'utilisation pour le moment. Veuillez réessayer plus tard.",
  unavailable: "Désolé, l'assistant n'est pas disponible pour le moment.",
  refused: 'Désolé, je ne peux pas vous aider avec cette demande.',
  privacyNotice: 'Les messages sont conservés pour fournir ce service.',
  privacyLinkLabel: 'Politique de confidentialité',
  typeAMessage: 'Écrivez un message...',
  sendMessage: 'Envoyer le message',
  openChat: 'Ouvrir la discussion',
  closeChat: 'Fermer la discussion',
  chatWithUs: 'Discutez avec nous',
  talkToSite: 'Parlez à notre site',
  chatDialogLabel: 'Discussion avec {name}',
  teamLabel: 'Équipe {name}',
  typing: "L'assistant écrit",
  tryAgain: 'Réessayer',
  newChat: 'Nouvelle discussion',
  confirmNewChat: 'Confirmer la nouvelle discussion — la conversation sera effacée',
  tapAgainToClear: 'Appuyez à nouveau pour effacer cette conversation',
  tapAgainToClearShort: 'Appuyez à nouveau pour effacer',
  expandChat: 'Agrandir la discussion',
  collapseChat: 'Réduire la discussion',
  dismissSuggestions: 'Masquer les suggestions',
  leaveDetails: 'Laisser vos coordonnées',
  leadTitle: 'Laissez vos coordonnées et nous vous recontacterons.',
  leadSubmit: 'Envoyer',
  leadSending: 'Envoi en cours…',
  leadCancel: 'Annuler',
  leadThanks: 'Merci {name} ! Nous avons bien reçu vos coordonnées et vous recontacterons bientôt.',
  leadNameRequired: 'Veuillez saisir votre nom.',
  leadEmailInvalid: 'Veuillez saisir une adresse e-mail valide.',
  leadTooLong: 'Le texte est trop long, veuillez le raccourcir.',
  leadError: "Désolé, nous n'avons pas pu envoyer vos coordonnées. Veuillez réessayer.",
  leadCap: 'Nous recevons beaucoup de demandes en ce moment. Veuillez réessayer plus tard.',
  leadMessageLabel: 'Message',
  leadMessageRequired: 'Veuillez saisir un message.',
  detailsOptional: '{label} (facultatif)',
  leadFieldRequired: 'Veuillez remplir le champ « {label} ».',
  leadChoose: 'Choisir…',
  bookingDetailsTitle: 'Vos coordonnées pour la réservation',
  detailsName: 'Nom',
  detailsEmail: 'E-mail',
  detailsPhone: 'Téléphone',
  bookingContinue: 'Continuer',
  bookingPhoneRequired: 'Veuillez saisir votre numéro de téléphone.',
  bookingUnderName: 'Réservation au nom de {name}',
  bookingDetailsSent: 'Voici mes coordonnées',
  bookingConfirmYes: 'Oui, réserver',
  bookingConfirmNo: 'Non',
  bookingBooked: 'Réservation confirmée',
  bookingReference: 'Référence {id}',
  sendFeedback: 'Donner votre avis',
  backToChat: 'Retour à la discussion',
  feedbackSubtitle: 'Votre avis sur cet assistant',
  feedbackIntro: "Votre avis est transmis à Ema Answers, l'équipe qui conçoit cet assistant.",
  feedbackTypeLabel: "De quoi s'agit-il ?",
  feedbackTypeGeneral: 'Général',
  feedbackTypeFeature: 'Suggestion',
  feedbackTypeBug: 'Problème',
  feedbackTypeLove: 'Compliment',
  feedbackMessage: 'Votre avis',
  feedbackPlaceholder: "Ce qui a marché, ce qui n'a pas marché, ce qui vous aiderait ?",
  feedbackEmail: 'E-mail (facultatif, si vous souhaitez une réponse)',
  feedbackSubmit: 'Envoyer mon avis',
  feedbackMessageRequired: "Veuillez d'abord écrire votre avis.",
  feedbackThanks: 'Merci, votre avis a bien été envoyé.',
  feedbackError: "Désolé, nous n'avons pas pu envoyer votre avis. Veuillez réessayer.",
  humanCheckTitle: 'Vérification de sécurité',
  humanCheckFailed: "Désolé, nous n'avons pas pu vérifier que vous êtes une personne. Veuillez réessayer.",
};

const de: WidgetStrings = {
  subtitle: 'KI-Assistent',
  greeting: 'Hallo! Wie kann ich Ihnen helfen?',
  suggestions: ['Welche Leistungen bieten Sie an?', 'Preise anzeigen', 'Mehr erfahren'],
  errorMessage: 'Entschuldigung, die Verbindung ist gestört. Bitte erneut versuchen.',
  originBlockedMessage: 'Dieser Assistent ist auf dieser Website nicht verfügbar.',
  noContent: 'Ich habe noch keine Informationen, um zu antworten. Bitte schauen Sie bald wieder vorbei.',
  overCap: 'Dieser Assistent hat sein Nutzungslimit vorerst erreicht. Bitte versuchen Sie es später erneut.',
  unavailable: 'Entschuldigung, der Assistent ist gerade nicht verfügbar.',
  refused: 'Entschuldigung, bei dieser Anfrage kann ich nicht helfen.',
  privacyNotice: 'Nachrichten werden gespeichert, um diesen Service bereitzustellen.',
  privacyLinkLabel: 'Datenschutzerklärung',
  typeAMessage: 'Nachricht schreiben...',
  sendMessage: 'Nachricht senden',
  openChat: 'Chat öffnen',
  closeChat: 'Chat schließen',
  chatWithUs: 'Schreiben Sie uns',
  talkToSite: 'Sprechen Sie mit unserer Website',
  chatDialogLabel: 'Chat mit {name}',
  teamLabel: 'Team {name}',
  typing: 'Der Assistent schreibt',
  tryAgain: 'Erneut versuchen',
  newChat: 'Neuer Chat',
  confirmNewChat: 'Neuen Chat bestätigen — das Gespräch wird gelöscht',
  tapAgainToClear: 'Erneut tippen, um dieses Gespräch zu löschen',
  tapAgainToClearShort: 'Erneut tippen zum Löschen',
  expandChat: 'Chat vergrößern',
  collapseChat: 'Chat verkleinern',
  dismissSuggestions: 'Vorschläge ausblenden',
  leaveDetails: 'Kontaktdaten hinterlassen',
  leadTitle: 'Hinterlassen Sie Ihre Kontaktdaten und wir melden uns bei Ihnen.',
  leadSubmit: 'Senden',
  leadSending: 'Wird gesendet…',
  leadCancel: 'Abbrechen',
  leadThanks: 'Danke, {name}! Wir haben Ihre Daten erhalten und melden uns bald.',
  leadNameRequired: 'Bitte geben Sie Ihren Namen ein.',
  leadEmailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
  leadTooLong: 'Der Text ist zu lang – bitte kürzen.',
  leadError: 'Entschuldigung, Ihre Daten konnten nicht gesendet werden. Bitte erneut versuchen.',
  leadCap: 'Wir erhalten gerade sehr viele Anfragen. Bitte versuchen Sie es später erneut.',
  leadMessageLabel: 'Nachricht',
  leadMessageRequired: 'Bitte geben Sie eine Nachricht ein.',
  detailsOptional: '{label} (optional)',
  leadFieldRequired: 'Bitte füllen Sie „{label}“ aus.',
  leadChoose: 'Bitte wählen…',
  bookingDetailsTitle: 'Ihre Angaben für die Buchung',
  detailsName: 'Name',
  detailsEmail: 'E-Mail',
  detailsPhone: 'Telefon',
  bookingContinue: 'Weiter',
  bookingPhoneRequired: 'Bitte geben Sie Ihre Telefonnummer ein.',
  bookingUnderName: 'Buchung auf den Namen {name}',
  bookingDetailsSent: 'Hier sind meine Angaben',
  bookingConfirmYes: 'Ja, buchen',
  bookingConfirmNo: 'Nein',
  bookingBooked: 'Buchung bestätigt',
  bookingReference: 'Referenz {id}',
  sendFeedback: 'Feedback senden',
  backToChat: 'Zurück zum Chat',
  feedbackSubtitle: 'Feedback zu diesem Assistenten',
  feedbackIntro: 'Ihr Feedback geht an Ema Answers, das Team hinter diesem Assistenten.',
  feedbackTypeLabel: 'Worum geht es?',
  feedbackTypeGeneral: 'Allgemein',
  feedbackTypeFeature: 'Vorschlag',
  feedbackTypeBug: 'Problem',
  feedbackTypeLove: 'Lob',
  feedbackMessage: 'Ihr Feedback',
  feedbackPlaceholder: 'Was hat funktioniert, was nicht, was würde helfen?',
  feedbackEmail: 'E-Mail (optional, falls Sie eine Antwort möchten)',
  feedbackSubmit: 'Feedback senden',
  feedbackMessageRequired: 'Bitte schreiben Sie zuerst Ihr Feedback.',
  feedbackThanks: 'Danke, Ihr Feedback wurde gesendet.',
  feedbackError: 'Entschuldigung, Ihr Feedback konnte nicht gesendet werden. Bitte erneut versuchen.',
  humanCheckTitle: 'Sicherheitsprüfung',
  humanCheckFailed: 'Entschuldigung, wir konnten nicht bestätigen, dass Sie ein Mensch sind. Bitte erneut versuchen.',
};

const es: WidgetStrings = {
  subtitle: 'Asistente de IA',
  greeting: '¡Hola! ¿En qué puedo ayudarte?',
  suggestions: ['¿Qué servicios ofrecen?', 'Ver precios', 'Cuéntame más'],
  errorMessage: 'Lo siento, tengo problemas de conexión. Inténtalo de nuevo.',
  originBlockedMessage: 'Este asistente no está disponible en este sitio.',
  noContent: 'Todavía no tengo información para responder. Vuelve a intentarlo pronto.',
  overCap: 'Este asistente ha alcanzado su límite de uso por ahora. Inténtalo de nuevo más tarde.',
  unavailable: 'Lo siento, el asistente no está disponible en este momento.',
  refused: 'Lo siento, no puedo ayudarte con esa solicitud.',
  privacyNotice: 'Los mensajes se guardan para prestar este servicio.',
  privacyLinkLabel: 'Política de privacidad',
  typeAMessage: 'Escribe un mensaje...',
  sendMessage: 'Enviar el mensaje',
  openChat: 'Abrir el chat',
  closeChat: 'Cerrar el chat',
  chatWithUs: 'Escríbenos',
  talkToSite: 'Habla con nuestra web',
  chatDialogLabel: 'Chat de {name}',
  teamLabel: 'Equipo de {name}',
  typing: 'El asistente está escribiendo',
  tryAgain: 'Reintentar',
  newChat: 'Nuevo chat',
  confirmNewChat: 'Confirmar el nuevo chat — se borrará la conversación',
  tapAgainToClear: 'Toca de nuevo para borrar esta conversación',
  tapAgainToClearShort: 'Toca de nuevo para borrar',
  expandChat: 'Ampliar el chat',
  collapseChat: 'Reducir el chat',
  dismissSuggestions: 'Ocultar las sugerencias',
  leaveDetails: 'Deja tus datos',
  leadTitle: 'Déjanos tus datos y nos pondremos en contacto contigo.',
  leadSubmit: 'Enviar',
  leadSending: 'Enviando…',
  leadCancel: 'Cancelar',
  leadThanks: '¡Gracias, {name}! Hemos recibido tus datos y te contactaremos pronto.',
  leadNameRequired: 'Introduce tu nombre.',
  leadEmailInvalid: 'Introduce una dirección de correo válida.',
  leadTooLong: 'El texto es demasiado largo; acórtalo, por favor.',
  leadError: 'Lo siento, no hemos podido enviar tus datos. Inténtalo de nuevo.',
  leadCap: 'Estamos recibiendo muchas consultas ahora mismo. Inténtalo de nuevo más tarde.',
  leadMessageLabel: 'Mensaje',
  leadMessageRequired: 'Escribe un mensaje.',
  detailsOptional: '{label} (opcional)',
  leadFieldRequired: 'Rellena el campo «{label}».',
  leadChoose: 'Elige…',
  bookingDetailsTitle: 'Tus datos para la reserva',
  detailsName: 'Nombre',
  detailsEmail: 'Correo electrónico',
  detailsPhone: 'Teléfono',
  bookingContinue: 'Continuar',
  bookingPhoneRequired: 'Introduce tu número de teléfono.',
  bookingUnderName: 'Reserva a nombre de {name}',
  bookingDetailsSent: 'Aquí tienes mis datos',
  bookingConfirmYes: 'Sí, reservar',
  bookingConfirmNo: 'No',
  bookingBooked: 'Reserva confirmada',
  bookingReference: 'Referencia {id}',
  sendFeedback: 'Enviar comentarios',
  backToChat: 'Volver al chat',
  feedbackSubtitle: 'Comentarios sobre este asistente',
  feedbackIntro: 'Tus comentarios llegan a Ema Answers, el equipo que crea este asistente.',
  feedbackTypeLabel: '¿De qué se trata?',
  feedbackTypeGeneral: 'General',
  feedbackTypeFeature: 'Sugerencia',
  feedbackTypeBug: 'Problema',
  feedbackTypeLove: 'Elogio',
  feedbackMessage: 'Tus comentarios',
  feedbackPlaceholder: '¿Qué ha funcionado, qué no y qué te ayudaría?',
  feedbackEmail: 'Correo electrónico (opcional, si quieres respuesta)',
  feedbackSubmit: 'Enviar comentarios',
  feedbackMessageRequired: 'Escribe primero tus comentarios.',
  feedbackThanks: 'Gracias, tus comentarios se han enviado.',
  feedbackError: 'Lo siento, no hemos podido enviar tus comentarios. Inténtalo de nuevo.',
  humanCheckTitle: 'Comprobación de seguridad',
  humanCheckFailed: 'Lo siento, no hemos podido confirmar que eres una persona. Inténtalo de nuevo.',
};

const STRINGS: Record<string, WidgetStrings> = { en, it, fr, de, es };

/**
 * The page's language, as the page itself declares it.
 *
 * `<html lang>` first: it is what the site AUTHOR said the page is written in,
 * and on a site with a language switcher it is the only thing that moves when
 * the visitor switches. `navigator.language` is the reader's preference, not
 * the page's, so it is the fallback rather than the source — a German browser
 * on an Italian page is reading Italian.
 */
export function detectLanguage(): string {
  if (typeof document !== 'undefined') {
    const declared = document.documentElement.getAttribute('lang');
    if (declared && declared.trim()) return declared.trim();
  }
  if (typeof navigator !== 'undefined' && navigator.language) return navigator.language;
  return 'en';
}

/** Copy for a language tag ('it', 'it-IT', 'IT'), English when unreviewed. */
export function stringsFor(tag: string): WidgetStrings {
  return STRINGS[tag.split('-')[0].toLowerCase()] ?? en;
}

/** `fill('{name} team', { name: 'Acme' })` → `'Acme team'`. Unknown keys are left as they are. */
export function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) =>
    Object.prototype.hasOwnProperty.call(values, key) ? values[key] : whole,
  );
}
